import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { Chips, type Counts } from "../components/Chips";
import { Empty } from "../components/Empty";
import { FilterSheet } from "../components/FilterSheet";
import { LoadMore } from "../components/LoadMore";
import { Row } from "../components/Row";
import { SearchBar } from "../components/SearchBar";
import { Wordmark } from "../components/Wordmark";
import { api, type Capture } from "../lib/api";
import { groupByBucket } from "../lib/format";
import { CATEGORY_META, Icon } from "../lib/icons";
import { useDebounced } from "../lib/useDebounced";

const UNDO_MS = 5000;

export function Timeline() {
  const [params, setParams] = useSearchParams();
  const category = params.get("category");
  const tag = params.get("tag");
  const urlQuery = params.get("q") ?? "";
  const [search, setSearch] = useState(urlQuery);
  const debounced = useDebounced(search, 220);
  const [sheetOpen, setSheetOpen] = useState(false);

  // Push debounced search into the URL so reloads + back-button work.
  useEffect(() => {
    const next = new URLSearchParams(params);
    if (debounced) next.set("q", debounced);
    else next.delete("q");
    if (next.toString() !== params.toString()) {
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);

  const captures = useInfiniteQuery({
    queryKey: ["captures", { category, tag, q: debounced }],
    queryFn: ({ pageParam }) =>
      api.listCaptures({
        category: category ?? undefined,
        tag: tag ?? undefined,
        q: debounced || undefined,
        before_id: pageParam,
      }),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (last) => last.next_before_id ?? undefined,
  });

  const categories = useQuery({
    queryKey: ["categories"],
    queryFn: api.listCategories,
  });

  const counts: Counts = useMemo(() => {
    const out: Counts = {};
    for (const c of categories.data?.categories ?? []) out[c.name] = c.count;
    return out;
  }, [categories.data]);

  // ── Delete with a deferred-commit undo window ──────────────────
  // The destructive request only fires after the undo timer elapses, so
  // nothing is lost during the window. Rows are hidden optimistically.
  const qc = useQueryClient();
  const [hidden, setHidden] = useState<Set<number>>(() => new Set());
  const [toast, setToast] = useState<Capture | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const pending = useRef<{ item: Capture; timer: number } | null>(null);

  const unhide = (id: number) =>
    setHidden((s) => {
      const n = new Set(s);
      n.delete(id);
      return n;
    });

  const performDelete = useCallback(
    (item: Capture) => {
      api
        .deleteCapture(item.id)
        .then(() => {
          qc.invalidateQueries({ queryKey: ["categories"] });
          qc.invalidateQueries({ queryKey: ["captures"] });
        })
        .catch(() => {
          unhide(item.id); // fail loud: restore the row + surface the error
          setErr("Couldn't delete — please try again.");
        });
    },
    [qc],
  );

  const flush = useCallback(() => {
    const p = pending.current;
    if (!p) return;
    clearTimeout(p.timer);
    pending.current = null;
    setToast(null);
    performDelete(p.item);
  }, [performDelete]);

  const requestDelete = useCallback(
    (item: Capture) => {
      setErr(null);
      flush(); // commit any prior pending immediately, then start a fresh one
      setHidden((s) => new Set(s).add(item.id));
      setToast(item);
      const timer = window.setTimeout(() => {
        pending.current = null;
        setToast(null);
        performDelete(item);
      }, UNDO_MS);
      pending.current = { item, timer };
    },
    [flush, performDelete],
  );

  const undo = useCallback(() => {
    const p = pending.current;
    if (!p) return;
    clearTimeout(p.timer);
    pending.current = null;
    unhide(p.item.id);
    setToast(null);
  }, []);

  // Don't lose a pending delete if the page unmounts — commit it now. The
  // component is gone, so refresh the shared caches here (else the deleted row
  // reappears on return) and report failures rather than swallowing them.
  useEffect(
    () => () => {
      const p = pending.current;
      if (p) {
        clearTimeout(p.timer);
        api
          .deleteCapture(p.item.id)
          .catch((e) => console.error(`Deleting capture ${p.item.id} failed`, e))
          .finally(() => {
            qc.invalidateQueries({ queryKey: ["captures"] });
            qc.invalidateQueries({ queryKey: ["categories"] });
          });
      }
    },
    [qc],
  );

  // Auto-dismiss the error toast so the UI never gets stuck.
  useEffect(() => {
    if (!err) return;
    const t = window.setTimeout(() => setErr(null), UNDO_MS);
    return () => clearTimeout(t);
  }, [err]);

  const items = useMemo(
    () =>
      (captures.data?.pages.flatMap((p) => p.items) ?? []).filter((i) => !hidden.has(i.id)),
    [captures.data, hidden],
  );
  const groups = useMemo(() => groupByBucket(items), [items]);

  const selectCategory = (next: string | null) => {
    const merged = new URLSearchParams(params);
    if (next) merged.set("category", next);
    else merged.delete("category");
    setParams(merged, { replace: true });
  };

  const clearTag = () => {
    const merged = new URLSearchParams(params);
    merged.delete("tag");
    setParams(merged, { replace: true });
  };

  const showEmpty =
    !captures.isLoading && !captures.isError && items.length === 0
      ? debounced
        ? "no-search"
        : category || tag
          ? "no-filter"
          : "fresh"
      : null;

  const activePlural = category ? CATEGORY_META[category]?.plural.toLowerCase() : null;

  return (
    <>
      <header className="header">
        <div className="col">
          <div className="header-inner mobile-search-row">
            <Wordmark />
            <SearchBar value={search} onChange={setSearch} />
            <Link className="ask-link" to="/ask" aria-label="Ask a question">
              <Icon.ask />
              <span>Ask</span>
            </Link>
            <button
              className="mobile-filter-trigger"
              onClick={() => setSheetOpen(true)}
              aria-label="Open filters"
            >
              <Icon.filter />
              <span>Filter</span>
              {activePlural ? (
                <span className="count-active">· {activePlural}</span>
              ) : null}
            </button>
          </div>
          <Chips active={category} counts={counts} onSelect={selectCategory} />
          {tag ? (
            <div className="active-tag">
              <span className="tag">{tag}</span>
              <button onClick={clearTag} aria-label="Clear tag filter">
                ✕
              </button>
            </div>
          ) : null}
        </div>
      </header>

      <main className="col" style={{ paddingBottom: 96 }}>
        {showEmpty ? (
          <Empty
            kind={showEmpty}
            query={debounced}
            category={category}
          />
        ) : (
          groups.map((g) => (
            <section key={g.label}>
              <div className="date-marker">{g.label}</div>
              <ul className="row-list">
                {g.items.map((item) => (
                  <Row
                    key={item.id}
                    item={item}
                    query={debounced}
                    onDelete={requestDelete}
                  />
                ))}
              </ul>
            </section>
          ))
        )}
        <LoadMore
          hasNextPage={captures.hasNextPage}
          isFetchingNextPage={captures.isFetchingNextPage}
          isError={captures.isError}
          fetchNextPage={captures.fetchNextPage}
        />
        <div className="archived-link">
          <Link to="/places">Places →</Link>
          <Link to="/tax">Tax →</Link>
          <Link to="/archived">View archived →</Link>
        </div>
      </main>

      <FilterSheet
        open={sheetOpen}
        active={category}
        counts={counts}
        onSelect={selectCategory}
        onClose={() => setSheetOpen(false)}
      />

      {err ? (
        <div className="toast toast-error" role="alert">
          <span>{err}</span>
        </div>
      ) : toast ? (
        <div className="toast" role="status">
          <span>Deleted “{toast.summary}”</span>
          <button className="toast-undo" onClick={undo}>
            Undo
          </button>
        </div>
      ) : null}
    </>
  );
}
